import React from 'react';
import {
  useConfirmationStep,
  ProductItem,
  LocationData,
  ProductData,
  ProductUnit,
} from './ConfirmationStep.hooks';
import { formatCurrency } from '../../../../utils/formatters/money';
import {
  ConfirmationContainer,
  ConfirmationHeader,
  ConfirmationTitle,
  ConfirmationDate,
  SummaryCard,
  SummaryCardContent,
  SummaryGrid,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  LocationInfo,
  LocationIcon,
  LocationText,
  ProductsTableContainer,
  ProductsTable,
  TableHeader,
  TableHeaderCell,
  ProductNameCell,
  ProductCodeCell,
  QuantityCell,
  ValidationError,
  ValidationErrorTitle,
  ValidationErrorList,
  ValidationErrorItem,
  EmptyState,
  SectionDivider,
  TableRow,
  TableBody,
} from './ConfirmationStep.styles';
import EmptyStateMessage from '../../../common/EmptyStateMessage';

/**
 * Note: For backward compatibility, the props still use "branch" naming,
 * but these are actually locations in the backend.
 */
export interface ConfirmationStepProps {
  productItems: ProductItem[];
  selectedBranch: string; // This is a location ID
  products: ProductData[];
  locations?: LocationData[]; // Using LocationData interface
  productUnits?: ProductUnit[];
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  productItems,
  selectedBranch: selectedLocationId, // Renamed for clarity in this component
  products,
  locations = [], // Renamed for clarity in this component
  productUnits = [],
}) => {
  const {
    totalQuantity,
    totalCostPrice,
    totalSalePrice,
    formattedDate,
    locationName,
    productsWithDetails,
    productStats,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    productSummary,
    hasValidData,
    validationMessages,
  } = useConfirmationStep({
    productItems,
    selectedBranch: selectedLocationId,
    products,
    locations,
    productUnits,
  });

  // Si no hay datos válidos, mostrar estado de error
  if (!hasValidData) {
    return (
      <ConfirmationContainer>
        <ConfirmationHeader>
          <ConfirmationTitle>Resumen de Registro</ConfirmationTitle>
          <ConfirmationDate>{formattedDate}</ConfirmationDate>
        </ConfirmationHeader>

        <ValidationError>
          <ValidationErrorTitle>No se puede mostrar el resumen</ValidationErrorTitle>
          <ValidationErrorList>
            {validationMessages.map((message, index) => (
              <ValidationErrorItem key={index}>{message}</ValidationErrorItem>
            ))}
          </ValidationErrorList>
        </ValidationError>
      </ConfirmationContainer>
    );
  }

  // Si no hay productos, mostrar estado vacío
  if (productItems.length === 0) {
    return (
      <ConfirmationContainer>
        <ConfirmationHeader>
          <ConfirmationTitle>Resumen de Registro</ConfirmationTitle>
          <ConfirmationDate>{formattedDate}</ConfirmationDate>
        </ConfirmationHeader>

        <EmptyState>
          <EmptyStateMessage message="No hay productos para mostrar" minHeight="auto" />
        </EmptyState>
      </ConfirmationContainer>
    );
  }

  return (
    <ConfirmationContainer>
      <ConfirmationHeader>
        <ConfirmationTitle>Resumen de Registro</ConfirmationTitle>
        <ConfirmationDate>{formattedDate}</ConfirmationDate>
      </ConfirmationHeader>

      <ProductsTableContainer>
        <ProductsTable>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Producto</TableHeaderCell>
              <TableHeaderCell>Código</TableHeaderCell>
              <TableHeaderCell align="center">Cantidad</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {productsWithDetails.map((item, _index) => (
              <TableRow key={item.id}>
                <ProductNameCell>{item.productName}</ProductNameCell>
                <ProductCodeCell>{item.productCode}</ProductCodeCell>
                <QuantityCell>{item.quantity}</QuantityCell>
              </TableRow>
            ))}
          </TableBody>
        </ProductsTable>
      </ProductsTableContainer>

      <SectionDivider />

      <SummaryCard>
        <SummaryCardContent>
          <SummaryGrid>
            <SummaryItem>
              <SummaryLabel>Total de Productos</SummaryLabel>
              <SummaryValue>{productStats.uniqueProducts}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Total de piezas</SummaryLabel>
              <SummaryValue>{totalQuantity}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Costo total</SummaryLabel>
              <SummaryValue style={{ color: '#3b82f6' }}>
                {formatCurrency(totalCostPrice)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Precio de Venta Total</SummaryLabel>
              <SummaryValue style={{ color: '#10b981' }}>
                {formatCurrency(totalSalePrice)}
              </SummaryValue>
            </SummaryItem>
          </SummaryGrid>

          <LocationInfo>
            <LocationIcon
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </LocationIcon>
            <LocationText>Ubicación: {locationName}</LocationText>
          </LocationInfo>
        </SummaryCardContent>
      </SummaryCard>
    </ConfirmationContainer>
  );
};

export default ConfirmationStep;
